import Dialog from '@comp/Dialog/Dialog';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import styled from '@emotion/styled';
import GoToArrow from '@icon/GoToArrow';
import Share from '@icon/Share';
import UtsavReferIcon from '@icon/UtsavReferIcon';
import { Fonts, mediaQuery } from '@styles/index';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

const StyledThankYouImage = styled(Image)`
  border-radius: 8.4px;
  margin-bottom: 24px;
  margin-right: 103.8px;
  margin-left: 103.8px;
  flex-shrink: 0;

  ${mediaQuery.uptoMobile} {
    margin-bottom: 16px;
    margin-right: 80.23px;
    margin-left: 80.23px;
    height: auto;
  }
`;
const ThankYouHeader = styled.h3`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H3};
  line-height: 32px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
  text-align: center;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    font-size: ${Fonts.LARGE};
    line-height: 24px;
  }
`;
const StyledThankYouContainer = styled.div`
  background: var(--color-base-1);
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  margin-bottom: -40px;
`;

const CodeImageWrapper = styled.div`
  position: relative;
  width: fit-content;
  margin-bottom: 24px;
  img {
    display: block;
  }
  h4 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: var(--color-text-primary);
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.H4};
    line-height: 26px;
    letter-spacing: -0.5%;
    white-space: nowrap;
  }
`;

const LotteryDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 25px;
  margin-left: 25px;
  margin-bottom: 24px;
  gap: 12px;
  justify-content: center;
  align-items: center;
`;
const RedirectionContainer = styled.div`
  border: 1px solid var(--color-divider);
  border-radius: 8px;
  padding-top: 15px;
  padding-bottom: 15px;
  padding-left: 40px;
  padding-right: 40px;
  display: flex;
  gap: 4px;
  height: 48px;
  align-items: center;
  justify-content: center;
  width: fit-content;
  cursor: pointer;

  ${mediaQuery.uptoMobile} {
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 12px;
    padding-right: 8px;
  }
`;
const ShareContainer = styled.div`
  border: 1px solid var(--color-divider);
  margin-bottom: 16px;
  border-radius: 8px;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 73.5px;
  padding-right: 73.5px;
  display: flex;
  gap: 4px;
  height: 48px;
  align-items: center;
  justify-content: center;
  width: fit-content;
  cursor: pointer;

  ${mediaQuery.uptoMobile} {
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 42px;
    padding-right: 38px;
  }
`;

const ButtonText = styled.p`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.SMALL};
  line-height: 18px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
`;
const ThankYouDescription = styled.p`
  max-width: 300px;
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.LARGE};
  line-height: 24px;
  letter-spacing: 0%;
  color: var(--color-text-secondary);
  text-align: center;

  ${mediaQuery.uptoMobile} {
    max-width: 100%;
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: 16px;
  }
`;

const DialogDescriptionContainer = styled.div`
  background: var(--color-i-icon-butter-light);
  margin-bottom: 24px;
  align-items: flex-start;
  width: 464px;
  display: flex;
  border-radius: 8px;
  padding: 12px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const DescText = styled.p`
  margin-left: 8px;
  color: var(--color-text-primary);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P2};
  line-height: 16px;
  letter-spacing: 0;
  text-align: left;
`;
const ShareDialogDescription = ({ displayText }) => {
  return (
    <DialogDescriptionContainer>
      <UtsavReferIcon size={32} />
      <DescText>{displayText}</DescText>
    </DialogDescriptionContainer>
  );
};
const UtsavThankYouCard = ({ data, activationCode, lotteryNumber }) => {
  const [shareLink, setShareLink] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (activationCode) {
        const baseUrl = window.location.href.split('?')[0];
        setShareLink(`${baseUrl}?utsav_referrer=${activationCode}`);
      } else {
        setShareLink(window.location.href);
      }
    }
  }, [activationCode]);

  const handleShareClick = () => {
    setShowShareDialog(true);
  };
  const handleRedirection = () => {
    window.open(data?.thankyouScreenData?.redirectionPlaceLink, '_blank');
  };
  return (
    <StyledThankYouContainer>
      <StyledThankYouImage
        src={data?.thankyouScreenData?.thankyouImage?.url}
        alt="lottery image"
        width={143.45}
        height={134.4}
      />
      <CodeImageWrapper>
        <Image
          src={data?.thankyouScreenData?.lotteryImageBackground?.url}
          alt="lottery code bg"
          width={140}
          height={41}
        />
        <h4>{lotteryNumber}</h4>
      </CodeImageWrapper>

      <LotteryDescriptionContainer>
        <ThankYouHeader>{data?.thankyouScreenData?.header}</ThankYouHeader>
        <ThankYouDescription>
          {data?.thankyouScreenData?.description}
        </ThankYouDescription>
      </LotteryDescriptionContainer>
      <ShareContainer onClick={handleShareClick}>
        <ButtonText>{data?.inviteCtaText}</ButtonText>
        <Share size={16} />
      </ShareContainer>
      <RedirectionContainer onClick={handleRedirection}>
        <Image
          src={data?.thankyouScreenData?.redirectionPlaceLogo?.url}
          alt="redirection place icon"
          width={23}
          height={16.5}
        />
        <ButtonText>
          {data?.thankyouScreenData?.redirectionPlaceText}
        </ButtonText>
        <GoToArrow size={20} />
      </RedirectionContainer>
      <Dialog
        dialogState={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      >
        <ShareDialog
          externalComponent={
            <ShareDialogDescription
              displayText={data?.thankyouScreenData?.shareText}
            />
          }
          showExternalComponent
          data={{ permalink: shareLink }}
          useDataLink
        />
      </Dialog>
    </StyledThankYouContainer>
  );
};

export default UtsavThankYouCard;
